<script setup lang="ts">
import AutoComplete from 'primevue/autocomplete'
import {
  buildExpenseTypeSuggestions,
  type ExpenseTypeOption,
  type ExpenseTypeSuggestion,
} from '~/components/Form/expenseTypeSuggestions'

const props = withDefaults(defineProps<{
  modelValue: number | null
  // When provided, the component uses this list instead of self-fetching
  // (e.g. the POS forms supply their own pos-expenses store list).
  options?: ExpenseTypeOption[]
  // When false, no inline "Create" row is offered (search-only).
  allowCreate?: boolean
}>(), {
  options: undefined,
  allowCreate: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
}>()

const { t } = useI18n()
const generalExpensesStore = useGeneralExpensesStore()
const expenseTypesStore = useExpenseTypesStore()

// Self-fetched list, only used when `options` prop is not provided.
const fetched = ref<ExpenseTypeOption[]>([])
const expenseTypes = computed<ExpenseTypeOption[]>(() => props.options ?? fetched.value)

const suggestions = ref<ExpenseTypeSuggestion[]>([])
// AutoComplete v-model holds the typed string or the selected suggestion object.
const selected = ref<ExpenseTypeSuggestion | string>('')

function syncDisplayFromModel() {
  if (props.modelValue == null) return
  const match = expenseTypes.value.find(tp => tp.value === props.modelValue)
  if (match) {
    selected.value = { value: match.value, label: match.label }
  }
}

async function loadTypes() {
  if (props.options === undefined) {
    fetched.value = await generalExpensesStore.fetchExpenseTypes()
  }
  syncDisplayFromModel()
}

function search(event: { query: string }) {
  suggestions.value = buildExpenseTypeSuggestions(expenseTypes.value, event.query, props.allowCreate)
}

async function onSelect(event: { value: ExpenseTypeSuggestion }) {
  const picked = event.value

  if (picked._isNew || picked.value === '__new__') {
    const ok = await expenseTypesStore.createExpenseType({ name: picked.label })
    if (ok) {
      fetched.value = await generalExpensesStore.fetchExpenseTypes()
      const created = fetched.value.find(tp => tp.label === picked.label)
      if (created) {
        selected.value = { value: created.value, label: created.label }
        emit('update:modelValue', created.value)
        return
      }
    }
    // Create failed (store already toasted) — reset selection.
    selected.value = ''
    emit('update:modelValue', null)
    return
  }

  emit('update:modelValue', picked.value as number)
}

watch(() => props.modelValue, syncDisplayFromModel)
// Resolve the display label once an externally-supplied list arrives.
watch(expenseTypes, syncDisplayFromModel)

onMounted(loadTypes)
</script>

<template>
  <AutoComplete
    v-model="selected"
    :suggestions="suggestions"
    option-label="label"
    :placeholder="t('Search expense type...')"
    :delay="300"
    :min-length="1"
    complete-on-focus
    dropdown
    class="w-full"
    @complete="search"
    @option-select="onSelect"
  >
    <template #option="{ option }">
      <div class="flex items-center gap-2">
        <template v-if="option._isNew">
          <i class="pi pi-plus-circle text-blue-500" />
          <span>{{ t('Create') }}: <strong>{{ option.label }}</strong></span>
        </template>
        <template v-else>
          <i class="pi pi-tag text-gray-500" />
          <span>{{ option.label }}</span>
        </template>
      </div>
    </template>
  </AutoComplete>
</template>
